<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import Button from "../common/Button.vue"

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const selectedYear = ref(new Date().getFullYear());
const data = ref(null);
const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October',
    'November', 'December'
];

// --- Computed ---

const years = computed(() => data.value?.years || []);
const monthTotals = computed(() => data.value?.monthTotals || null);
const yearTotals = computed(() => data.value?.yearTotals || null);

// --- Helpers ---

const numberFormat = (value) => {
    return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value || 0);
};

// --- Methods ---

const requestData = () => {
    store.dispatch('request/post', {
            url: 'api/OrdersController/getReportsData',
            postData: {
                year: selectedYear.value
            }
        })
        .then((obj) => {
            data.value = obj.data;
        });
};

const setYear = (year) => {
    selectedYear.value = year;
};

const showMonth = (i) => {
    store.dispatch('modals/show', {
        componentName: 'OrdersReportsModal',
        obj: {
            month: i + 1,
            year: selectedYear.value,
            monthName: monthNames[i]
        }
    });
};

// --- Watchers ---

// Automatically re-fetch data when the year is changed
watch(selectedYear, () => {
    requestData();
});

// --- Lifecycle ---

onMounted(() => {
    store.commit('navigationComponent', 'm_basket_reports');
    requestData();
});
</script>

<template>
    <div v-if="data">
        <div class="p-3 text-center">
                <Button v-for="year in years" :key="year" :active-value="selectedYear === year" @click.native="setYear(year)">
                    {{ year }}
                </Button>
        </div>

        <div class="card">
            <div class="card-body">
                <table class="table table-condensed table-striped">
                    <thead>
                    <tr>
                        <th>Month</th>
                        <th class="text-end">Orders</th>
                        <th class="text-end">Net</th>
                        <th class="text-end">VAT</th>
                        <th class="text-end">Gross</th>
                        <th class="text-end">Shipping</th>
                        <th class="text-end">Total</th>
                    </tr>
                    </thead>

                    <tbody>
                    <tr v-for="(monthName, i) in monthNames" :key="i">
                        <template v-if="monthTotals">
                            <td>
                                <a v-if="monthTotals[i]" href @click.prevent="showMonth(i)">
                                    {{ monthName }}
                                </a>
                                <span v-else>{{ monthName }}</span>
                            </td>
                            <td class="text-end">
                                {{ monthTotals[i]?.orders || 0 }}
                            </td>
                            <td class="text-end">
                                &pound;{{ numberFormat(monthTotals[i]?.net || 0) }}
                            </td>
                            <td class="text-end">
                                &pound;{{ numberFormat(monthTotals[i]?.tax || 0) }}
                            </td>
                            <td class="text-end">
                                &pound;{{ numberFormat(monthTotals[i]?.gross || 0) }}
                            </td>
                            <td class="text-end">
                                &pound;{{ numberFormat(monthTotals[i]?.shipping || 0) }}
                            </td>
                            <td class="text-end">
                                &pound;{{ numberFormat(monthTotals[i]?.total || 0) }}
                            </td>
                        </template>
                    </tr>
                    </tbody>

                    <tfoot v-if="yearTotals">
                    <tr>
                        <th>Total</th>
                        <th class="text-end">{{ yearTotals.orders }}</th>
                        <th class="text-end">&pound;{{ numberFormat(yearTotals.net) }}</th>
                        <th class="text-end">&pound;{{ numberFormat(yearTotals.tax) }}</th>
                        <th class="text-end">&pound;{{ numberFormat(yearTotals.gross) }}</th>
                        <th class="text-end">&pound;{{ numberFormat(yearTotals.shipping) }}</th>
                        <th class="text-end">&pound;{{ numberFormat(yearTotals.total) }}</th>
                    </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>

    <div v-else class="p-5 text-center">
        <div class="spinner-border text-primary" role="status"></div>
        <div class="mt-2 text-muted">Loading sales data...</div>
    </div>
</template>

<style scoped>
/* Scoped styles preserved */
.active {
    font-weight: bold;
}
</style>